// StaggeredReveal.tsx
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Children, isValidElement, useEffect, useMemo, useState, type ReactNode } from 'react';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const animations = { fadeInUp };

const Container = styled.div<{ $running: boolean }>`
    /* So grid layout from parent (Group) still applies to children */
    display: contents;
`;

const AnimatedItem = styled.div<{
    $duration: number;
    $animation: keyof typeof animations;
    $running: boolean;
    $fullWidth?: boolean;
}>`
    display: ${(p) => (p.$fullWidth ? 'flex-grow' : 'flex')};
    height: 100%;
    opacity: 0;
    will-change: opacity, transform;

    @media (prefers-reduced-motion: no-preference) {
        ${(p) => css`
            animation-name: ${animations[p.$animation]};
            animation-duration: ${p.$duration}ms;
            animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
            animation-fill-mode: both;
            animation-delay: var(--delay, 0ms);
            animation-play-state: ${p.$running ? 'running' : 'paused'};
        `}
    }

    @media (prefers-reduced-motion: reduce) {
        opacity: 1;
    }
`;

export interface StaggeredRevealProps {
    children: ReactNode;
    trigger?: boolean;
    staggerDelay?: number; // ms between items
    baseDuration?: number; // ms per item
    animation?: keyof typeof animations;
    startDelay?: number; // initial wait
    onComplete?: () => void;
    fullWidth?: boolean;
}

export default function StaggeredReveal({
    children,
    trigger = true,
    staggerDelay = 120,
    baseDuration = 420,
    animation = 'fadeInUp',
    startDelay = 0,
    onComplete,
    fullWidth = false,
}: StaggeredRevealProps) {
    const items = useMemo(() => Children.toArray(children).filter(Boolean), [children]);
    const [running, setRunning] = useState(false);

    useEffect(() => {
        setRunning(false);
        if (!trigger) return;
        const t = window.setTimeout(() => {
            // double RAF ensures styles are committed before we flip running
            const r1 = requestAnimationFrame(() => {
                const r2 = requestAnimationFrame(() => setRunning(true));
                return () => cancelAnimationFrame(r2);
            });
            return () => cancelAnimationFrame(r1);
        }, startDelay);
        return () => clearTimeout(t);
    }, [trigger, startDelay, items.length]);

    useEffect(() => {
        if (!running || items.length === 0) return;
        const total = (items.length - 1) * staggerDelay + baseDuration;
        const t = window.setTimeout(() => onComplete?.(), total);
        return () => clearTimeout(t);
    }, [running, items.length, staggerDelay, baseDuration, onComplete]);

    return (
        <Container $running={running}>
            {items.map((child, i) => (
                <AnimatedItem
                    key={i}
                    $duration={baseDuration}
                    $animation={animation}
                    $running={running}
                    $fullWidth={fullWidth}
                    style={{ ['--delay' as any]: `${i * staggerDelay}ms` }}>
                    {isValidElement(child) ? child : <div>{child}</div>}
                </AnimatedItem>
            ))}
        </Container>
    );
}
